import React, { useEffect } from 'react';
import { Box, IconButton, Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import FullscreenExitRoundedIcon from '@mui/icons-material/FullscreenExitRounded';

interface FullscreenImageViewerProps {
  images: string[];
  initialIndex: number;
  onClose: () => void;
}

const FullscreenImageViewer: React.FC<FullscreenImageViewerProps> = ({
  images,
  initialIndex,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = React.useState(initialIndex);
  const [showThumbnails, setShowThumbnails] = React.useState(true);

  const toggleThumbnails = () => setShowThumbnails(prev => !prev);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        prevImage();
      } else if (e.key === 'ArrowRight') {
        nextImage();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line
  }, [currentIndex]);

  const prevImage = () => {
    setCurrentIndex(idx => (idx === 0 ? images.length - 1 : idx - 1));
  };

  const nextImage = () => {
    setCurrentIndex(idx => (idx === images.length - 1 ? 0 : idx + 1));
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        bgcolor: 'rgba(0,0,0,0.85)',
        zIndex: 1300,
      }}
    >
      {/* Top bar */}
      <Box
        sx={{
          position: 'fixed',
          top: 16,
          left: 16,
          right: 16,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          zIndex: 1500,
        }}
      >
        <IconButton
          onClick={toggleThumbnails}
          sx={{
            color: 'white',
            bgcolor: 'rgba(0,0,0,0.4)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            display: { xs: 'none', sm: 'none', md: 'inline-flex' },
          }}
          aria-label={showThumbnails ? 'Hide thumbnails' : 'Show thumbnails'}
        >
          {showThumbnails ? (
            <FullscreenExitRoundedIcon />
          ) : (
            <FullscreenRoundedIcon />
          )}
        </IconButton>

        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            bgcolor: 'rgba(0,0,0,0.4)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
          }}
          aria-label='Close'
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Left arrow */}
      <IconButton
        onClick={prevImage}
        sx={{
          position: 'fixed',
          top: '50%',
          left: 16,
          color: 'white',
          bgcolor: 'rgba(0,0,0,0.4)',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
          transform: 'translateY(-50%)',
          zIndex: 1500,
        }}
        aria-label='Previous Image'
      >
        <ArrowBackIosNewRoundedIcon />
      </IconButton>

      {/* Right arrow */}
      <IconButton
        onClick={nextImage}
        sx={{
          position: 'fixed',
          top: '50%',
          right: 16,
          color: 'white',
          bgcolor: 'rgba(0,0,0,0.4)',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
          transform: 'translateY(-50%)',
          zIndex: 1500,
        }}
        aria-label='Next Image'
      >
        <ArrowForwardIosRoundedIcon />
      </IconButton>

      {/* Centered main image */}
      <Box
        component='img'
        src={images[currentIndex]}
        alt={`Image ${currentIndex + 1}`}
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          maxHeight: '80vh',
          maxWidth: '90vw',
          objectFit: 'contain',
          borderRadius: 2,
          boxShadow: 24,
          transform: 'translate(-50%, -50%)',
          zIndex: 1400,
        }}
      />

      {/* Bottom bar (image count + thumbnails) */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 16,
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          zIndex: 1500,
          px: 2,
        }}
      >
        {/* Image count */}
        <Box sx={{ color: 'white', fontWeight: 600, fontSize: '1.1rem' }}>
          {currentIndex + 1} / {images.length}
        </Box>

        {/* Thumbnail strip */}
        {showThumbnails && (
          <Grid
            container
            spacing={1}
            sx={{
              maxWidth: '90vw',
              justifyContent: 'center',
              flexWrap: 'wrap',
              overflowY: 'auto',
              maxHeight: '20vh',
              px: 1,
              '&::-webkit-scrollbar': { height: 8 },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: 'rgba(255, 255, 255, 0.3)',
                borderRadius: 1,
              },
              display: { xs: 'none', sm: 'none', md: 'flex' },
            }}
          >
            {images.map((img, idx) => (
              <Grid
                // item
                key={idx}
                sx={{
                  border:
                    idx === currentIndex
                      ? '2px solid #fff'
                      : '2px solid transparent',
                  borderRadius: 1,
                  cursor: 'pointer',
                  flexShrink: 0,
                  width: 80,
                  height: 50,
                  overflow: 'hidden',
                  '&:hover': { opacity: 0.8 },
                }}
                onClick={() => setCurrentIndex(idx)}
              >
                <Box
                  component='img'
                  src={img}
                  alt={`Thumbnail ${idx + 1}`}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default FullscreenImageViewer;
